/** @jsx isolateComponent */

import type { ReactNode } from 'react';
import { css } from '@emotion/core';
import Markdown from 'markdown-to-jsx';

import { isolateComponent, labelStyles } from '@zapier/style-encapsulation';
import {
  Button,
  Modal,
  ModalContent,
  Link,
  Typography,
  Colors,
} from '@zapier/design-system';

type Props = {
  /**
   * Title of the policy.
   */
  title: string;
  /**
   * A description of what's changed in the policy.
   */
  tldr: string;
  /**
   * URL of the policy page to view more.
   */
  url?: string;
  /**
   * Callback for when acceptance button is clicked.
   */
  onAccept: () => void;
  /**
   * Function to render markdown.
   */
  renderMarkdown?: (markdown: string) => ReactNode;
};

const gap = '15px';

const Styles = labelStyles('PolicyModal', {
  content: css`
    display: grid;
    grid-template-columns: 100%;
    grid-template-rows: auto;
    gap: ${gap};
    justify-items: center;
    padding: 30px;
    text-align: center;
    ${Typography.bodyText800};
    color: ${Colors.grey700};
  `,
  title: css`
    ${Typography.header800};
    color: ${Colors.black};
  `,
  markdown: css`
    text-align: left;

    p {
      &:not(:last-child) {
        margin: 0 0 ${gap};
      }
    }

    h2,
    h3,
    h4,
    h5,
    h6 {
      margin-bottom: ${gap};
      &:not(:first-of-type) {
        margin-top: ${gap};
      }
    }

    ul {
      margin-left: 20px;
      list-style: disc;
    }
  `,
});

const defaultProps = {
  renderMarkdown: (markdown: string) => <Markdown>{markdown}</Markdown>,
};

export const PolicyModal = (_props: Props) => {
  const props = {
    ...defaultProps,
    ..._props,
  };
  return (
    <Modal canClose={false} onClosed={() => {}}>
      <ModalContent>
        <section css={Styles.content}>
          <h1 css={Styles.title}>We’ve updated the {props.title}</h1>
          <div css={Styles.markdown}>{props.renderMarkdown(props.tldr)}</div>
          <Button casing="uppercase" hasShadow={true} onClick={props.onAccept}>
            Accept
          </Button>
          {!!props.url && (
            <Link href={props.url} target="_blank">
              Review the updated {props.title}
            </Link>
          )}
        </section>
      </ModalContent>
    </Modal>
  );
};

PolicyModal.defaultProps = defaultProps;
